<template>
    <section v-editable="blok" class="t-partners">
        <div class="t-partners__container container">
            <h4 class="t-partners__title">{{ blok.title }}</h4>
            <div class="t-partners__list-wrapper">
                <SliderBtn
                    btn-type="prev"
                    class="t-partners__arrow t-partners__arrow--left"
                    :class="{ 'is-active': !isAtLeftMargin }"
                    aria-label="Scroll to the beginning of the list"
                    @click="scrollList(-500)"
                />
                <ul ref="list" class="t-partners__list" @scroll="showArrows">
                    <li v-for="logo in blok.logos" :key="logo.id" class="t-partners__list-item">
                        <NuxtImg
                            v-if="logo?.filename"
                            :src="logo.filename"
                            :title="logo.title"
                            :alt="logo.alt"
                            provider="storyblok"
                            format="webp"
                            loading="lazy"
                            class="t-partners__list-image"
                        />
                    </li>
                </ul>
                <SliderBtn
                    btn-type="next"
                    class="t-partners__arrow t-partners__arrow--right"
                    :class="{ 'is-active': !isAtRightMargin }"
                    aria-label="Scroll to the end of the list"
                    @click="scrollList(500)"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core';
import type { L4TrustedPartners } from '~/types/components/L4TrustedPartners';

const { blok } = defineProps<{ blok: L4TrustedPartners }>();

const listContainerWidth = ref(0);
const isAtLeftMargin = ref(true);
const isAtRightMargin = ref(false);
const list = ref<HTMLUListElement | null>(null);

onMounted(() => {
    nextTick(() => {
        if (!list.value) return;
        listContainerWidth.value = list.value.getBoundingClientRect().width;
    });
});

function scrollList(pixels) {
    const el = list.value;
    if (el) {
        el.scrollTo({
            left: pixels,
            behavior: 'smooth',
        });
    }
    ``;
}

const showArrows = useThrottleFn((e) => {
    const container = e.target;
    const scrollPosition = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    isAtRightMargin.value = scrollPosition >= scrollWidth - listContainerWidth.value - 10;
    isAtLeftMargin.value = scrollPosition <= 10;
}, 30);
</script>

<style lang="postcss" scoped>
.t-partners {
    @apply pt-8 pb-16 bg-white;
    &__container {
        @apply tablet:flex px-4 md:px-0 items-center;
    }
    &__title {
        color: #180b1e;
        @apply mb-3 tablet:mb-0 tablet:mr-14 font-bold text-base;
    }
    &__list-wrapper {
        @apply relative grow;
    }
    &__list {
        @apply overflow-auto flex justify-between gap-3 sm:gap-0 w-full;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar {
            display: none;
        }
        &-item {
            @apply shrink-0;
        }
    }

    &__list-image {
        @apply h-6 tablet:h-8 w-auto;
    }
    &__arrow {
        @apply sm:hidden absolute top-1/2 -translate-y-1/2 opacity-0 pointer-events-none;
        filter: drop-shadow(0px 0px 8px rgba(131, 126, 126, 0.2));
        &--left {
            @apply -left-4;
        }
        &--right {
            @apply -right-4;
        }
        &.is-active {
            @apply opacity-100 pointer-events-auto;
        }
    }
}
</style>
